import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridFilterModel,
  GridFilterItem,
  getGridStringOperators,
} from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { fetchSites } from 'store/sitesSlice';
import { gamesService } from '../api/games';
import { commonGridStyles } from 'styles/gridStyles';
import CustomPagination from 'components/common/pagination/pagination';
import GameStatsExportButtons from './GameStatsExportButtons';
import styles from './games.module.scss';
import commonStyles from 'styles/common.module.scss';
import { RootState } from 'store';

const GameStatistics: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );

  const [stats, setStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedSite, setSelectedSite] = useState<string | number>('');

  const [sortField, setSortField] = useState<string>('id');
  const [sortValue, setSortValue] = useState<'ASC' | 'DESC'>('DESC');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [filters, setFilters] = useState<Record<string, any>>({});

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await gamesService.getGameStats({
        page,
        limit,
        sortField,
        sortValue,
        siteId: selectedSite === 'all' ? undefined : selectedSite,
        filters: '',
        ...filters,
      });

      if (response?.data?.docs) {
        setStats(response.data.docs);
        setTotal(response.data.totalDocs || 0);
      }
    } catch (error: any) {
      console.error('Failed to fetch game stats:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to fetch game statistics',
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedSite, sortField, sortValue, filters, dispatch]);

  useEffect(() => {
    if (!sites || sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    } else if (sites.length > 0 && selectedSite === '') {
      setSelectedSite(sites[0].id);
    }
  }, [sites, selectedSite, dispatch]);

  useEffect(() => {
    if (selectedSite !== '') {
      fetchStats();
    }
  }, [fetchStats, selectedSite, sortField, sortValue, filters]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field);
      setSortValue(model[0].sort?.toUpperCase() as 'ASC' | 'DESC');
    } else {
      setSortField('id');
      setSortValue('DESC');
    }
  }, []);

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    const newFilters: Record<string, any> = {};
    model.items.forEach((item: GridFilterItem) => {
      if (item.field && item.value !== undefined && item.value !== '') {
        newFilters[item.field] = item.value;
      }
    });
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'gameId',
      headerName: 'Game ID',
      width: 90,
      sortable: true,
      filterOperators: customStringOperators,
    },
    {
      field: 'title',
      headerName: 'Game Title',
      flex: 1,
      minWidth: 200,
      headerClassName: 'align-left-header',
      cellClassName: 'align-left-cell',
      sortable: true,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            justifyContent: 'flex-start',
          }}
        >
          <Box
            component="img"
            src={
              params.row.gameImageUrl ||
              'https://cdn.zmsfnxxx.xyz/i/s6/pragmaticexternal/ThreeKindoms.webp'
            }
            alt={params.value}
            sx={{ width: 40, height: 28, borderRadius: 1, objectFit: 'cover' }}
          />
          <Typography noWrap variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'income',
      headerName: 'Income',
      width: 140,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Typography sx={{ color: '#a6e22e', fontSize: '14px', lineHeight: '100%' }}>
          + ${Number(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      width: 140,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Typography sx={{ color: '#f92672', fontSize: '14px', lineHeight: '100%' }}>
          - ${Number(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'ggr',
      headerName: 'GGR',
      width: 140,
      filterOperators: customStringOperators,
      renderCell: (params) => {
        const val = Number(params.value);
        return (
          <Typography
            sx={{
              color: val >= 0 ? '#a6e22e' : '#f92672',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '100%',
            }}
          >
            {val >= 0 ? '+$' : '-$'}
            {Math.abs(val).toLocaleString()}
          </Typography>
        );
      },
    },
    {
      field: 'sessionCount',
      headerName: 'Sessions',
      width: 80,
      filterOperators: customStringOperators,
    },
    {
      field: 'roundCount',
      headerName: 'Rounds',
      width: 100,
      filterOperators: customStringOperators,
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={page - 1}
      onPageChange={(_, newPage) => setPage(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
      }}
      rowsPerPageOptions={[10, 20, 50, 100]}
    />
  );

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Game Statistics
        </Typography>

        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={selectedSite}
                label="Site"
                onChange={(e) => {
                  setSelectedSite(e.target.value as string | number);
                  setPage(1);
                }}
                className={styles.select}
                disabled={sitesLoading}
              >
                <MenuItem value="all">All Sites</MenuItem>
                {sites?.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <GameStatsExportButtons
            siteId={selectedSite === '' || selectedSite === 'all' ? null : Number(selectedSite)}
          />
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={stats}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.gameId}
          autoHeight
          sx={{
            ...commonGridStyles,
            '& .align-left-header .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start !important',
            },
            '& .align-left-cell': {
              justifyContent: 'flex-start !important',
            },
          }}
          disableRowSelectionOnClick
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          rowCount={total}
          filterModel={filterModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 4,
                  color: 'var(--text-secondary)',
                }}
              >
                No game statistics found
              </Box>
            ),
          }}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default GameStatistics;
